import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useEffect, useState } from "react";
import { css } from "styled-components";

interface EndYokeOpeningScreenProps {
    fields?: any;
}

interface EndYokeOpeningScreenStyles {

    wrapper?: InjectableCss;
    formWrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    searchButton?: ButtonPresentationProps;
}

export const findEndYokeStyles: EndYokeOpeningScreenStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 24px;
            padding-bottom: 24px;
        `,
    },
    formWrapper: {
        css: css`
            width: 100%;
            max-width: 340px;
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    heading: {
        variant: "h2",
    },
    searchButton: {
        variant: "primary",
    },
};

const defaultAssemblyData: string[] = ["AUXILIARY TRANSMISSIONS", "SINGLE AXLES", "SPLIT SHAFT PTO", "TANDEM AXLES", "TRANSFER CASES", "TRANSMISSIONS"];

const EndYokeOpeningScreen: React.FC<EndYokeOpeningScreenProps> = ({
    fields
}) => {
    const styles = findEndYokeStyles;
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
    const [selectedAssembly, setSelectedAssembly] = useState("");
    const [selectedMake, setSelectedMake] = useState("");
    const [selectedModel, setSelectedModel] = useState("");
    const [assemblyData, setAssemblyData] = useState<string[]>(defaultAssemblyData);
    const [makesData, setMakesData] = useState<string[]>([]);
    const [modelsData, setModelsData] = useState<string[]>([]);

    const fetchMakes = async (assembly: string) => {
        setShowLoadingOverlay(true);
        try {
            const result = await fetch(`/api/v1/findEndYoke/makes?assembly=${assembly}`);
            if (!result.ok) {
                throw new Error(result.statusText);
            }
            const data = await result.json();
            setMakesData(data);

        } catch (error) {
            console.error("Error fetching makes:", error);
        } finally {
            setShowLoadingOverlay(false);
        }
    };

    const fetchModels = async (assembly: string, make: string) => {
        setShowLoadingOverlay(true);
        try {
            const result = await fetch(`/api/v1/findEndYoke/models?assembly=${assembly}&make=${make}`);
            if (!result.ok) {
                throw new Error(result.statusText);
            }
            const data = await result.json();
            setModelsData(data);

        } catch (error) {
            console.error("Error fetching models:", error);
        } finally {
            setShowLoadingOverlay(false);
        }
    };

    const search = () => {
        if (!selectedAssembly || !selectedMake || !selectedModel) return;

        const params = new URLSearchParams({
            page_no: "1",
            assemblyObj: selectedAssembly,
            make: selectedMake,
            model: selectedModel,
        });
        window.location.search = `?${params.toString()}`;
    }

    const handleAssemblyChange: SelectProps["onChange"] = event => {
        const selectedValue = event.target.value;
        setSelectedMake("");
        setSelectedModel("");
        setSelectedAssembly(selectedValue);
        fetchMakes(selectedValue);
    }

    const handleManufacturerChange: SelectProps["onChange"] = event => {
        const selectedValue = event.target.value;
        setSelectedModel("");
        setSelectedMake(selectedValue);
        fetchModels(selectedAssembly, selectedValue);
    }

    const handleModelChange: SelectProps["onChange"] = event => {
        setSelectedModel(event.target.value);
    }

    return (
        <LoadingOverlay
            loading={showLoadingOverlay}
            css={css`
                        width: 100%;
                    `}
        >
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Typography {...styles.heading} as="h2">
                    {translate("Find End Yoke/Flange")}
                </Typography>
                <StyledWrapper {...styles.formWrapper} className="form-wrapper">
                    <Select
                        label={translate("Assembly")}
                        name="assembly"
                        onChange={handleAssemblyChange}
                    >
                        <option value="">{translate("Select Assembly")}</option>
                        {assemblyData && assemblyData.map(s => (
                            <option key={s} value={s}> 
                                {s}
                            </option>
                        ))}
                    </Select>
                    <Select
                        label={translate("Manufacturer")}
                        name="manufacturer"
                        onChange={handleManufacturerChange}
                        disabled={!selectedAssembly}
                    >
                        <option value="">{translate("Select Manufacturer")}</option>
                        {makesData && makesData.map(s => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </Select>
                    <Select
                        label={translate("Model")}
                        name="model"
                        onChange={handleModelChange}
                        disabled={!selectedMake || !selectedAssembly}
                    >
                        <option value="">{translate("Select Model")}</option>
                        {modelsData && modelsData.map(s => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </Select>
                    <Button {...styles.searchButton} onClick={search} disabled={!selectedAssembly || !selectedMake || !selectedModel}>
                        {translate("GO")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    )
};

export default EndYokeOpeningScreen;